import { useState, useEffect } from "react";
import { Search, Menu, X } from "lucide-react";
import { m, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import PrefetchLink from "@/components/PrefetchLink";
import logo from "@/assets/logo.webp";

const navItems = [
  { label: "Home", href: "/" },
  { label: "News", href: "/category/news" },
  { label: "Politics", href: "/category/politics" },
  
  { label: "Entertainment", href: "/category/entertainment" },
  { label: "SPORTS", href: "/category/sport" },
  { label: "Health", href: "/category/health" },
];

const SiteHeader = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchOpen(false);
      setSearchQuery("");
    }
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className={`sticky top-0 z-50 transition-shadow duration-300 ${scrolled ? "shadow-md" : ""}`}>
      {/* Top accent bar */}
      <div className="h-1 bg-secondary" />

      <div className="bg-nav text-nav-foreground border-b border-border">
        <div className="container flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="shrink-0">
            <img src={logo} alt="Periodical Today" className="h-12 w-auto dark:invert-0 invert" width={140} height={48} />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-0.5">
            {navItems.map((item) => (
              <PrefetchLink
                key={item.label}
                to={item.href}
                className="relative px-3 py-2 text-sm font-semibold uppercase tracking-wide text-nav-foreground/70 hover:text-nav-foreground transition-colors group"
              >
                {item.label}
                <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 group-hover:w-4/5 h-0.5 bg-secondary transition-all duration-300 rounded-full" />
              </PrefetchLink>
            ))}
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => setSearchOpen(!searchOpen)}
              className="p-2 text-nav-foreground/60 hover:text-secondary transition-colors rounded-full hover:bg-nav-foreground/5"
              aria-label="Search"
            >
              <Search className="w-5 h-5" />
            </button>
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden p-2 text-nav-foreground/60 hover:text-secondary transition-colors rounded-full hover:bg-nav-foreground/5"
              aria-label="Menu"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Search bar */}
        <AnimatePresence>
          {searchOpen && (
            <m.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden border-t border-nav-foreground/10"
            >
              <div className="container py-3">
                <form onSubmit={handleSearch} className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-nav-foreground/60" />
                  <label htmlFor="header-search" className="sr-only">Search articles</label>
                  <input
                    id="header-search"
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search articles..."
                    aria-label="Search articles"
                    className="w-full bg-nav-foreground/5 text-nav-foreground placeholder:text-nav-foreground/60 pl-10 pr-4 py-2.5 rounded-md text-sm font-body focus:outline-none focus:ring-2 focus:ring-secondary border border-nav-foreground/10"
                    autoFocus
                  />
                </form>
              </div>
            </m.div>
          )}
        </AnimatePresence>

        {/* Mobile nav */}
        <AnimatePresence>
          {mobileOpen && (
            <m.nav
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="md:hidden overflow-hidden border-t border-nav-foreground/10"
            >
              {navItems.map((item, i) => (
                <m.div
                  key={item.label}
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: i * 0.05 }}
                >
                  <PrefetchLink
                    to={item.href}
                    onClick={() => setMobileOpen(false)}
                    className="block px-6 py-3 text-sm font-semibold uppercase tracking-wide text-nav-foreground/70 hover:text-secondary hover:bg-nav-foreground/5 transition-colors border-b border-nav-foreground/5 last:border-0"
                  >
                    {item.label}
                  </PrefetchLink>
                </m.div>
              ))}
            </m.nav>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};

export default SiteHeader;
